
import React from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { PlayCircle, ArrowRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { ROUTES } from '@/constants/routes';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { ClickableAvatar } from '@/components/ui/clickable-avatar';
import { getInitials } from '@/utils/stringUtils';
import { formatDistanceToNow } from 'date-fns';
import { getThumbnailUrl } from '@/utils/youtubeUtils';
import type { FeedPost } from '@/hooks/useFeed';

export const RecentFeedWidget: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  const { data: posts } = useQuery({
    queryKey: ['feed-recent', user?.id],
    queryFn: async () => {
      if (!user) return [];
      const { data, error } = await supabase.rpc('get_user_feed', {
        p_user_id: user.id,
        p_cursor: null,
        p_limit: 3,
      });
      if (error) throw error;
      return (data || []) as FeedPost[];
    },
    enabled: !!user,
    staleTime: 60_000,
  });

  if (!posts || posts.length === 0) return null;

  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base flex items-center gap-2">
            <PlayCircle className="h-4 w-4 text-primary" />
            Recent Posts
          </CardTitle>
          <Button variant="ghost" size="sm" onClick={() => navigate(ROUTES.FEED)} className="text-xs gap-1">
            See all <ArrowRight className="h-3 w-3" />
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {posts.map((post) => (
          <button
            key={post.id}
            onClick={() => navigate(ROUTES.FEED)}
            className="flex gap-3 w-full text-left hover:bg-muted/50 rounded-lg p-2 -mx-2 transition-colors"
          >
            {post.youtube_video_id ? (
              <img
                src={getThumbnailUrl(post.youtube_video_id, 'default')}
                alt=""
                className="h-12 w-16 rounded object-cover shrink-0"
              />
            ) : (
              <ClickableAvatar userId={post.author_id} avatarUrl={post.author_avatar || undefined} fullName={post.author_name} size="sm" />
            )}
            <div className="flex-1 min-w-0">
              <p className="text-xs font-medium text-foreground truncate">{post.author_name}</p>
              <p className="text-xs text-muted-foreground truncate">{post.content || 'Shared a video'}</p>
              <p className="text-[10px] text-muted-foreground mt-0.5">
                {formatDistanceToNow(new Date(post.created_at), { addSuffix: true })}
              </p>
            </div>
          </button>
        ))}
      </CardContent>
    </Card>
  );
};
